import React, { createContext, useEffect, useState } from 'react';
import { useQueryParam, StringParam, NumberParam, ArrayParam } from 'use-query-params';
import {
  ExposedContextData,
  InterpolatedData,
  Topic,
  GetTopicsResponse,
  GetSavedSearchesResponse,
  ExtendedSavedSearch,
  AppliedSavedSearch,
  Article,
  GetArticlesResponse,
} from '../types/news_search.types';
import { getTopics, getArticles } from '../services/news_search.service';
import {
  getBookmarkedSearches
} from '../../services/bookmarked_searches/bookmarked_searches.service';
import { parseFiltersForApi } from '../utilities/news_search.utilities';
import AlertPopup from '../../components/react/alert_popup/alert_popup';
import { AlertDataType } from '../../components/react/alert_popup/types';
import { GENERIC_ERROR_MESSAGE } from '../../components/react/alert_popup/constants';

interface Props extends InterpolatedData {
  children: React.ReactNode;
}

const Context = createContext<ExposedContextData>(null);

const ContextProvider: React.FC<Props> = ({ children, ...props }: Props) => {
  const { isSignedIn, trendingNewsTopics, userId } = props;
  const [pageNumber = 1, setPageNumber] = useQueryParam<number>('page[number]', NumberParam);
  const [pageTotal, setPageTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [query, setQuery] = useQueryParam('query[]', StringParam);
  const [queryInput = '', setQueryInput] = useState<string>(query);
  const [postedDate, setPostedDate] = useQueryParam('filter[published_since][]', StringParam);
  const [authors, setAuthors] = useQueryParam('filter[authors][]', ArrayParam);
  const [topics, setTopics] = useQueryParam('filter[news_topics][]', ArrayParam);
  const [savedSearchId, setSavedSearchId] = useQueryParam('bookmarked_search_id', StringParam);
  const [sponsored, setSponsored] = useQueryParam('filter[sponsored]', StringParam);
  const [sortingField, setSortingField] = useQueryParam('sorting[field]', StringParam);
  const [appliedSavedSearch, setAppliedSavedSearch] = useState<AppliedSavedSearch>({});
  const [topicOptions, setTopicOptions] = useState<string[]>([]);
  const [savedSearches, setSavedSearches] = useState<ExtendedSavedSearch[]>([]);
  const [displaySavedSearches, setDisplaySavedSearches] = useState(false);
  const [articles, setArticles] = useState<Article[]>([]);
  const [showStatusModal, setShowStatusModal] = useState<boolean>(false);
  const [editedSavedSearch, setEditedSavedSearch] = useState<AppliedSavedSearch>({});
  const [showDeleteSearchModal, setShowDeleteSearchModal] = useState<boolean>(false);
  const [searchToBeDeleted, setSearchToBeDeleted] = useState<AppliedSavedSearch>({});
  const [alertData, setAlertData] = useState<AlertDataType|null>(null);

  useEffect((): void => {
    setIsLoading(true);
    getTopics()
      .then((response: GetTopicsResponse): void => {
        setTopicOptions(response.categories.map((entry: Topic): string => entry.name));
      })
      .finally((): void => {
        setIsLoading(false);
      });
  }, []);

  useEffect((): void => {
    if (isSignedIn) {
      setIsLoading(true);
      getBookmarkedSearches('articles', 300)
        .then((response: GetSavedSearchesResponse): void => {
          setSavedSearches(
            response
              .data
              // Only keep the saved searches that belongs to current user
              // In other words, exclude the ones from coleagues
              .filter((search) => search.consultant_id === userId)
              .map((search) => ({
                ...search,
                alerts: search.alerts.map((alert) => ({
                  ...alert,
                  periodicityDisplay: alert.how_often === 1 ? 'Daily' : 'Weekly',
                  isMyAlert: alert.consultant_id === userId,
                })),
              }))
          );
          if (savedSearchId) {
            const matchingSearch = response.data.find((search) => {
              return `${search.id}` === savedSearchId;
            });
            setAppliedSavedSearch(matchingSearch || {});
          }
        })
        .finally((): void => {
          setIsLoading(false);
        });
    }
  }, [isSignedIn]);

  const clearQueryRelatedFilter = (): void => {
    setQuery(undefined);
    setQueryInput('');
  };

  const clearNonRootFilters = (): void => {
    setAuthors([]);
    setPostedDate(undefined);
    setTopics([]);
    setSponsored(undefined);
  };

  const clearAllFilters = (): void => {
    clearQueryRelatedFilter();
    clearNonRootFilters();
    setSortingField(undefined);
    setPageNumber(1);
    setSavedSearchId(undefined);
    setAppliedSavedSearch({});
  };

  useEffect((): void => {
    if (Object.keys(appliedSavedSearch).length) {
      setPageNumber(1);
      setDisplaySavedSearches(false);
      // eslint-disable-next-line no-shadow
      const { query, filter, sorting } = appliedSavedSearch.params;
      setSortingField(sorting?.field);

      if (query && query?.length) {
        setQuery(query[0]);
        setQueryInput(query[0]);
      } else {
        clearQueryRelatedFilter();
      }

      if (filter && Object.keys(filter).length) {
        setAuthors(filter?.authors || []);
        setTopics(filter?.news_topics || []);
        setPostedDate(filter?.published_since ? filter?.published_since[0] : undefined);
        setSponsored(filter?.sponsored ? 'true' : undefined);
      } else {
        clearNonRootFilters();
      }
    }
  }, [appliedSavedSearch]);

  useEffect((): void => {
    setIsLoading(true);

    getArticles(
      parseFiltersForApi({
        query,
        postedDate,
        authors,
        topics,
        sponsored,
        sortingField,
        pageNumber,
      })
    )
      .then((response: GetArticlesResponse) => {
        setArticles(response.data);
        setPageTotal(response.page.pages);
      })
      .catch(() => setAlertData({ message: GENERIC_ERROR_MESSAGE, type: 'error' }))
      .finally((): void => {
        setIsLoading(false);
      });
  }, [query, postedDate, authors, topics, sortingField, pageNumber]);

  const exposedContextData = {
    isLoading,
    setIsLoading,
    queryInput,
    setQueryInput,
    query,
    setQuery,
    setPageNumber,
    postedDate,
    setPostedDate,
    authors,
    setAuthors,
    topics,
    setTopics,
    topicOptions,
    isSignedIn,
    userId,
    savedSearches,
    setSavedSearches,
    displaySavedSearches,
    setDisplaySavedSearches,
    savedSearchId,
    setSavedSearchId,
    clearAllFilters,
    appliedSavedSearch,
    setAppliedSavedSearch,
    sponsored,
    trendingNewsTopics,
    sortingField,
    setSortingField,
    articles,
    pageNumber,
    pageTotal,
    setPageTotal,
    showStatusModal,
    setShowStatusModal,
    editedSavedSearch,
    setEditedSavedSearch,
    showDeleteSearchModal,
    setShowDeleteSearchModal,
    searchToBeDeleted,
    setSearchToBeDeleted,
    alertData,
    setAlertData,
  };

  return (
    <>
      <AlertPopup alertData={alertData} setAlertData={setAlertData} />
      <Context.Provider value={exposedContextData}>{children}</Context.Provider>
    </>
  );
};

export { Context, ContextProvider };
